import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Select from 'react-select'
import { selectStyles } from '@astro/astrologer-forms'
import { api } from '@astro/api'
import { appUrl } from '@astro/config'
import './BlogPage.css'
import PageLayout from '../components/layout/PageLayout'
import Reveal from '../components/common/Reveal'
import BlogCard, { BlogCardSkeleton } from '../components/sections/blog/BlogCard'
import { decoratePost } from '../components/sections/blog/blogShared'

const PER_PAGE = 6

/* Keys are the API's own `sort` values (see api.blog.posts) — the server does
   the ordering, so there is nothing to translate here. */
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'popular', label: 'Most Read' },
  { value: 'title', label: 'A–Z' },
]

/* Shared react-select skin, adjusted to the filter card's .bp-input surface. */
const bpSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({
    ...selectStyles.control(base, state),
    background: '#FBF6EC',
    borderColor: state.isFocused ? 'rgba(217,119,6,.5)' : 'rgba(120,53,15,.14)',
    borderWidth: 1,
    borderRadius: 14,
    minHeight: 46,
    fontSize: 13,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(245,158,11,.12)' : 'none',
  }),
}

function MiniPostItem({ post }) {
  return (
    <Link to={`/blog/${post.slug ?? post.id}`} className="bp-mini-item">
      {post.img ? (
        <img className="bp-mini-thumb" src={post.img} alt="" loading="lazy" />
      ) : (
        <span className="bp-mini-thumb bp-mini-thumb--empty" aria-hidden="true">ॐ</span>
      )}
      <div>
        <p className="bp-mini-title">{post.title}</p>
        <p className="bp-mini-meta">{post.date}</p>
      </div>
    </Link>
  )
}

export default function BlogPage() {
  const gridRef = useRef(null)
  // ?category=<slug> is a real entry point: the article page's tag and its
  // Categories list link here, and that link has to arrive filtered.
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState(searchParams.get('category') ?? '')
  const [sort, setSort] = useState('newest')
  const [tab, setTab] = useState('recent')
  const [page, setPage] = useState(1)

  /* Keep the URL and the dropdown in step, so the filtered view is
     shareable and the browser's Back button steps through filters. */
  const chooseCategory = (slug) => {
    setCategory(slug)
    setPage(1)
    const next = new URLSearchParams(searchParams)
    if (slug) next.set('category', slug)
    else next.delete('category')
    setSearchParams(next, { replace: true })
  }

  // A link arriving from another page (or Back) carries the category in the
  // URL, not in this component's state.
  const urlCategory = searchParams.get('category') ?? ''
  useEffect(() => {
    setCategory((current) => (current === urlCategory ? current : urlCategory))
  }, [urlCategory])

  // One page at a time, filtered and sorted by the API.
  //
  // This used to walk up to TEN pages of /blog-posts on every visit and then
  // filter, sort and paginate the whole archive in the browser, because the
  // endpoint supported none of those. It does now (`search`, `category`,
  // `sort`, `per_page`), so this is one request per view — and the archive can
  // grow past 100 posts without the page quietly dropping the rest.
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['blog-posts', 'list', { page, search, category, sort }],
    queryFn: () =>
      api.blog.posts({
        page,
        per_page: PER_PAGE,
        sort,
        ...(search ? { search } : {}),
        ...(category ? { category } : {}),
      }),
    // Paging through the grid should not blank it between pages.
    placeholderData: (previous) => previous,
  })

  /* The sidebar's two shelves and the Editor's Pick are the archive's
     highlights, not this page's rows, so they are their own small reads and
     the search box does not disturb them. "Popular" is a real ordering now
     (the API counts article reads); it used to be posts 4-6 of the newest. */
  const { data: newestData, isPending: newestPending } = useQuery({
    queryKey: ['blog-posts', 'newest'],
    queryFn: () => api.blog.posts({ page: 1, per_page: 4, sort: 'newest' }),
  })
  const { data: popularData } = useQuery({
    queryKey: ['blog-posts', 'popular'],
    queryFn: () => api.blog.posts({ page: 1, per_page: 3, sort: 'popular' }),
  })
  const { data: categories = [] } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.blog.categories(),
  })

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'All Categories' },
      ...(Array.isArray(categories) ? categories : []).map((c) => ({ value: c.slug, label: c.name })),
    ],
    [categories],
  )

  const posts = useMemo(() => (data?.data ?? []).map(decoratePost), [data])
  const newest = useMemo(() => (newestData?.data ?? []).map(decoratePost), [newestData])
  const popular = useMemo(() => (popularData?.data ?? []).map(decoratePost), [popularData])

  // The newest article leads the page, and the shelf under it is the next
  // three — so the Editor's Pick is not repeated right beneath itself.
  const featured = newest[0]
  const recentPosts = newest.slice(1, 4)
  const popularPosts = popular.length ? popular : recentPosts

  const total = data?.total ?? 0
  const totalPages = Math.max(1, data?.last_page ?? 1)
  const rangeStart = data?.from ?? 0
  const rangeEnd = data?.to ?? 0

  const applySearch = () => {
    setSearch(searchInput.trim())
    setPage(1)
  }

  const goPage = (next) => {
    setPage(next)
    const el = gridRef.current
    if (el) window.scrollTo({ top: el.offsetTop - 120, behavior: 'smooth' })
  }

  return (
    <PageLayout className="blog-page text-astroDark antialiased overflow-x-hidden">

      {/* ── HERO / BREADCRUMB BANNER ── */}
      <header className="relative pt-30 sm:pt-32.5 lg:pt-37.5 pb-32 lg:pb-40 overflow-hidden bg-[#5A0808] text-white text-center">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] bg-cosmicRed/40 rounded-full animate-aura blur-3xl" />
          <svg
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square opacity-[0.06] text-goldPrimary animate-spin-slow"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="100" cy="100" r="98" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="100" cy="100" r="50" strokeWidth="0.5" />
            <path d="M100 2v196M2 100h196" strokeWidth="0.3" />
          </svg>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-goldPrimary/40" />
            <span className="text-[9px] uppercase font-bold tracking-[0.4em] text-goldPrimary/70 font-royal">Vedic Wisdom Journal</span>
            <span className="w-8 h-px bg-goldPrimary/40" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-royal text-[#FBF3E4] leading-tight">
            Sacred{' '}
            <span className="bg-linear-to-r from-goldPrimary via-amber-300 to-goldAccent bg-clip-text text-transparent">Insights</span>{' '}
            &amp; Vedic Wisdom
          </h1>
          <p className="text-sm sm:text-base text-white/50 font-light mt-5 max-w-xl mx-auto leading-relaxed">
            Authentic astrology, gemstone science, Vastu, numerology, and spiritual rituals — curated by Maharaj Ji to guide your journey toward clarity.
          </p>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-7 px-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40">
            <Link to="/" className="hover:text-goldPrimary transition-colors">Home</Link>
            <span className="text-goldPrimary/50">/</span>
            <span className="text-goldPrimary">Blog</span>
          </nav>
        </div>
      </header>

      {/* ── SEARCH & FILTER — FLOATING CARD ── */}
      <section className="relative z-20 px-6">
        <div className="max-w-6xl mx-auto -mt-24 lg:-mt-28">
          <Reveal className="bp-filter-card">

            {/* Search row */}
            <form
              className="flex flex-col sm:flex-row gap-3 mb-6"
              onSubmit={(e) => {
                e.preventDefault()
                applySearch()
              }}
            >
              <div className="relative flex-1">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-700/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" />
                  <path strokeLinecap="round" d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="search"
                  className="bp-input bp-input--search"
                  placeholder="Search articles by keyword…"
                  aria-label="Search blog"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <button type="submit" className="bp-search-btn">Search</button>
            </form>

            {/* Filters row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-6 mb-6 border-b border-amber-900/10">
              <div>
                <span className="bp-chip-label" id="blog-category-label">Category</span>
                <Select
                  inputId="blog-category"
                  aria-labelledby="blog-category-label"
                  options={categoryOptions}
                  value={categoryOptions.find((o) => o.value === category) ?? categoryOptions[0]}
                  onChange={(option) => chooseCategory(option?.value ?? '')}
                  styles={bpSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div>
                <span className="bp-chip-label" id="blog-sort-label">Sort By</span>
                <Select
                  inputId="blog-sort"
                  aria-labelledby="blog-sort-label"
                  options={SORT_OPTIONS}
                  value={SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0]}
                  onChange={(option) => {
                    setSort(option?.value ?? 'newest')
                    setPage(1)
                  }}
                  styles={bpSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {/* Recent / Popular tabs */}
            <div>
              <div className="flex items-center gap-6 mb-4">
                <button
                  type="button"
                  className={`bp-tab-btn${tab === 'recent' ? ' bp-tab-active' : ''}`}
                  onClick={() => setTab('recent')}
                >
                  Recent Posts
                </button>
                <button
                  type="button"
                  className={`bp-tab-btn${tab === 'popular' ? ' bp-tab-active' : ''}`}
                  onClick={() => setTab('popular')}
                >
                  Popular Posts
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8">
                {(tab === 'recent' ? recentPosts : popularPosts).map((p) => (
                  <MiniPostItem key={p.id} post={p} />
                ))}
              </div>
            </div>

          </Reveal>
        </div>
      </section>

      {/* ── FEATURED BLOG ── */}
      <section className="relative py-20 lg:py-24" style={{ background: '#FDFAF5' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <Reveal className="flex items-center gap-3 mb-10">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-amber-700/70">Editor&apos;s Pick</span>
            <span className="flex-1 h-px bg-amber-900/10" />
          </Reveal>
          {newestPending && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center animate-pulse" aria-hidden="true">
              <div className="lg:col-span-7 rounded-[28px] aspect-16/11" style={{ background: 'rgba(180,83,9,.10)' }} />
              <div className="lg:col-span-5 space-y-5">
                <div className="h-3 w-1/2 rounded" style={{ background: 'rgba(180,83,9,.10)' }} />
                <div className="h-8 w-full rounded" style={{ background: 'rgba(180,83,9,.14)' }} />
                <div className="h-3 w-5/6 rounded" style={{ background: 'rgba(180,83,9,.08)' }} />
                <div className="h-3 w-4/6 rounded" style={{ background: 'rgba(180,83,9,.08)' }} />
              </div>
            </div>
          )}
          {!newestPending && featured && (
            <Reveal as="article" className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <Link
                to={`/blog/${featured.slug ?? featured.id}`}
                className="lg:col-span-7 relative rounded-[28px] overflow-hidden shadow-2xl group block"
                style={{ boxShadow: '0 30px 70px -28px rgba(90,8,8,0.35)' }}
              >
                {featured.img && (
                  <img
                    src={featured.img}
                    alt={featured.title}
                    className="w-full h-full object-cover aspect-16/11 transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 bg-linear-to-t from-black/50 via-black/0 to-transparent" />
                <span className="bp-badge absolute top-5 left-5">{featured.badge}</span>
              </Link>
              <div className="lg:col-span-5 space-y-5">
                <div className="flex items-center flex-wrap gap-3 text-[10px] uppercase tracking-widest font-semibold text-amber-700/70">
                  <span className="flex items-center gap-1.5">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 2v4M16 2v4" />
                      <rect width="18" height="18" x="3" y="4" rx="2" />
                      <path d="M3 10h18" />
                    </svg>
                    {featured.date}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-amber-700/30" />
                  <span className="flex items-center gap-1.5">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {featured.readTime}
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-[34px] text-astroDark leading-tight">{featured.title}</h2>
                <p className="text-[13.5px] text-stone-500 font-light leading-relaxed">{featured.excerpt}</p>
                <div className="flex items-center gap-3 pt-1">
                  {featured.authorPhoto ? (
                    <img
                      src={featured.authorPhoto}
                      alt=""
                      loading="lazy"
                      className="w-9 h-9 rounded-full object-cover border border-goldPrimary/30"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-goldGlow/20 border border-goldPrimary/30 flex items-center justify-center text-amber-700 font-bold text-xs font-royal">
                      {(featured.author?.[0] || 'R').toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-semibold text-astroDark">{featured.author}</p>
                    {/* The author's own credential, not one title printed over
                        every article — astrologers publish here too. */}
                    {featured.authorCredential && (
                      <p className="text-[10px] text-stone-400">{featured.authorCredential}</p>
                    )}
                  </div>
                </div>
                <Link
                  to={`/blog/${featured.slug ?? featured.id}`}
                  className="shine-btn inline-flex items-center gap-2.5 bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[.2em] px-8 py-3.5 rounded-full shadow-xl transition-all duration-300 hover:scale-105 mt-2"
                >
                  Read Full Article
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* ── BLOG GRID ── */}
      <section ref={gridRef} className="relative py-4 lg:py-8 pb-20" style={{ background: '#FDFAF5' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between gap-4 mb-10">
            <h2 className="text-2xl sm:text-3xl text-astroDark">All Articles</h2>
            {!isPending && !isError && (
              <span className="text-xs text-stone-400 font-medium">
                {total === 0
                  ? 'No articles found'
                  : `Showing ${rangeStart}–${rangeEnd} of ${total} articles`}
              </span>
            )}
          </div>

          {isError && (
            <div className="py-14 text-center">
              <div className="text-5xl mb-4 opacity-40">🛰️</div>
              <p className="font-royal text-[17px] font-bold text-[#3B2A1A] mb-1">Couldn&apos;t Load Articles</p>
              <p className="text-sm mb-5" style={{ color: '#8B7355' }}>
                Something went wrong while fetching live data. Please try again.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
                style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
              >
                Retry
              </button>
            </div>
          )}

          {!isError && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {isPending
                ? Array.from({ length: PER_PAGE }, (_, i) => <BlogCardSkeleton key={i} />)
                : posts.map((post) => (
                    <Reveal key={post.id} className="h-full">
                      <BlogCard post={post} />
                    </Reveal>
                  ))}
            </div>
          )}

          {!isPending && !isError && total === 0 && (
            <p className="text-center text-sm text-stone-400 font-light py-10">
              No articles match your search. Try a different keyword or category.
            </p>
          )}

          {/* Pagination */}
          {!isPending && !isError && totalPages > 1 && (
            <div className="flex items-center justify-center flex-wrap gap-3 mt-14">
              <button type="button" className="bp-nav-btn" disabled={page === 1} onClick={() => goPage(page - 1)}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M12 7H2M2 7L6 3M2 7L6 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`bp-page-btn${n === page ? ' bp-page-active' : ''}`}
                  onClick={() => goPage(n)}
                >
                  {n}
                </button>
              ))}
              <button type="button" className="bp-nav-btn" disabled={page === totalPages} onClick={() => goPage(page + 1)}>
                Next
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section
        className="relative overflow-hidden py-20 lg:py-24"
        style={{ background: 'radial-gradient(ellipse 120% 100% at 30% 30%, #7C3A06 0%, #3E1503 35%, #160701 68%, #0A0300 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div style={{ position: 'absolute', top: '-100px', right: '-80px', width: '420px', height: '420px', background: 'radial-gradient(circle,rgba(245,158,11,.14) 0%,transparent 70%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: '-90px', left: '-70px', width: '380px', height: '380px', background: 'radial-gradient(circle,rgba(180,83,9,.18) 0%,transparent 70%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(245,158,11,.35),transparent)' }} />
        </div>
        <Reveal className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <span className="text-[20px] text-goldPrimary/70">ॐ</span>
          <h2 className="text-3xl sm:text-4xl text-[#FBF3E4] mt-3">Ready for Personal Guidance?</h2>
          <p className="text-sm text-white/50 font-light mt-4 leading-relaxed max-w-md mx-auto">
            Move from reading to clarity — book a one-on-one consultation with Maharaj Ji, or reach out with your questions.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <a
              href={appUrl('jyotish', 'consultations')}
              className="shine-btn bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[.2em] px-8 py-3.5 rounded-full shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              Book Consultation
            </a>
            <Link
              to="/contact"
              className="border border-white/40 hover:border-white text-white font-semibold text-[11px] uppercase tracking-[.22em] px-8 py-3.5 rounded-full transition-all hover:bg-white/10"
            >
              Contact Us
            </Link>
          </div>
        </Reveal>
      </section>

    </PageLayout>
  )
}
